import React from "react";
import { FileConfig } from "../types/process";

export const POLLING_INTERVAL = 300000; // 5 minutes
export const WEBSOCKET_TOKEN_REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes
export const TASK_FEEDBACK_DEBOUNCE = 20000; // 20 seconds

export const COLORS = {
  primary: "#36276F",
  secondary: "#EEEEEE",
  white: "#FFFFFF",
  textDark: "#333333",
  textLight: "#F8F9FA",
  success: "#28A745",
  warning: "#FFC107",
  danger: "#DC3545",
  info: "#17A2B8",
  gray: "#6c757d",
};

export const JESS_INFO_TEXT =
  "✨ New processes identified by Jess will be automatically added here.";

export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];
export const ALLOWED_PDF_TYPE = ["application/pdf"];
export const DEFAULT_ALLOWED_FILE_TYPES = [
  ...ALLOWED_IMAGE_TYPES,
  ...ALLOWED_PDF_TYPE,
];

export const AUDIO_INPUT_TYPE = "audio/webm";

export const DEFAULT_FILE_CONFIG: FileConfig = {
  maxFiles: 5,
  maxSizeMB: 5, // Max size per file
  allowedTypes: DEFAULT_ALLOWED_FILE_TYPES,
};

interface IconProps {
  className?: string;
  title?: string;
}

export const QuestionMarkIcon: React.FC<IconProps> = ({
  className: propClassName,
  title,
}) =>
  React.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      strokeWidth: "1.5",
      stroke: "currentColor",
      className: propClassName || "w-5 h-5",
      "aria-hidden": title ? undefined : "true",
      role: title ? "img" : undefined,
    },
    title && React.createElement("title", null, title),
    React.createElement("path", {
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z",
    }),
  );

export const CheckCircleIcon: React.FC<IconProps> = ({
  className: propClassName,
  title,
}) =>
  React.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      strokeWidth: "1.5",
      stroke: "currentColor",
      className: propClassName || "w-5 h-5 text-green-500",
      "aria-hidden": title ? undefined : "true",
      role: title ? "img" : undefined,
    },
    title && React.createElement("title", null, title),
    React.createElement("path", {
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
    }),
  );

export const ExclamationCircleIcon: React.FC<IconProps> = ({
  className: propClassName,
  title,
}) =>
  React.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      strokeWidth: "1.5",
      stroke: "currentColor",
      className: propClassName || "w-5 h-5 text-yellow-500",
      "aria-hidden": title ? undefined : "true",
      role: title ? "img" : undefined,
    },
    title && React.createElement("title", null, title),
    React.createElement("path", {
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z",
    }),
  );

export const PlusIcon: React.FC<IconProps> = ({
  className: propClassName,
  title,
}) =>
  React.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      strokeWidth: "1.5",
      stroke: "currentColor",
      className: propClassName || "w-5 h-5",
      "aria-hidden": title ? undefined : "true",
      role: title ? "img" : undefined,
    },
    title && React.createElement("title", null, title),
    React.createElement("path", {
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M12 4.5v15m7.5-7.5h-15",
    }),
  );

export const TrashIcon: React.FC<IconProps> = ({
  className: propClassName,
  title,
}) =>
  React.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      strokeWidth: "1.5",
      stroke: "currentColor",
      className: propClassName || "w-5 h-5",
      "aria-hidden": title ? undefined : "true",
      role: title ? "img" : undefined,
    },
    title && React.createElement("title", null, title),
    React.createElement("path", {
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12.56 0c-.34-.059-.68-.114-1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0",
    }),
  );

export const CloseIcon: React.FC<IconProps> = ({
  className: propClassName,
  title,
}) =>
  React.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      strokeWidth: "1.5",
      stroke: "currentColor",
      className: propClassName || "w-6 h-6",
      "aria-hidden": title ? undefined : "true",
      role: title ? "img" : undefined,
    },
    title && React.createElement("title", null, title),
    React.createElement("path", {
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M6 18 18 6M6 6l12 12",
    }),
  );

export const FeedbackBubbleIcon: React.FC<IconProps> = ({
  className: propClassName,
  title,
}) =>
  React.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 20 20",
      fill: "currentColor",
      className: propClassName || "w-5 h-5",
      "aria-hidden": title ? undefined : "true",
      role: title ? "img" : undefined,
    },
    title && React.createElement("title", null, title),
    React.createElement("path", {
      fillRule: "evenodd",
      d: "M10 2c-4.418 0-8 3.134-8 7s3.582 7 8 7c1.235 0 2.406-.224 3.48-.636A1 1 0 0 1 14 16.14V18a1 1 0 0 0 .624.928A7.996 7.996 0 0 0 18 16c0-3.866-3.582-7-8-7zM5 9a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2H6a1 1 0 0 1-1-1zm1 3a1 1 0 1 0 0 2h5a1 1 0 1 0 0-2H6z",
      clipRule: "evenodd",
    }),
  );

export const InfoIcon: React.FC<IconProps> = ({
  className: propClassName,
  title,
}) =>
  React.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      strokeWidth: "1.5",
      stroke: "currentColor",
      className: propClassName || "w-5 h-5 text-gray-400",
      "aria-hidden": title ? undefined : "true",
      role: title ? "img" : undefined,
    },
    title && React.createElement("title", null, title),
    React.createElement("path", {
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z",
    }),
  );

export const FileUploadIcon: React.FC<IconProps> = ({
  className: propClassName,
  title,
}) =>
  React.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      strokeWidth: "1.5",
      stroke: "currentColor",
      className: propClassName || "w-5 h-5",
      "aria-hidden": title ? undefined : "true",
      role: title ? "img" : undefined,
    },
    title && React.createElement("title", null, title),
    React.createElement("path", {
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.338-2.32 5.75 5.75 0 011.344 11.09A4.5 4.5 0 0112 21.75H6.75z",
    }),
  );

export const PaperClipIcon: React.FC<IconProps> = ({
  className: propClassName,
  title,
}) =>
  React.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      strokeWidth: "1.5",
      stroke: "currentColor",
      className: propClassName || "w-5 h-5",
      "aria-hidden": title ? undefined : "true",
      role: title ? "img" : undefined,
    },
    title && React.createElement("title", null, title),
    React.createElement("path", {
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.687 7.687a1.5 1.5 0 0 0 2.121 2.121l7.687-7.687a1.5 1.5 0 0 0-2.121-2.121Z",
    }),
  );
